import React, { useEffect, useState, useCallback } from 'react';
import { notificationService, deploymentService, clientService, appService } from '../../services/api';
import { Plus, Trash2, Toggle, Bell, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import NotificationRuleModal from './NotificationRuleModal';
import './Notifications.css';

const TRIGGER_LABELS = {
  deployment_offline: 'Deployment Offline', deployment_online: 'Deployment Online (Recovery)',
  deployment_degraded: 'Deployment Degraded', cpu_threshold: 'CPU Threshold',
  mem_threshold: 'Memory Threshold', disk_threshold: 'Disk Threshold',
  check_failed: 'Check Failed', incident_created: 'Incident Created',
  incident_resolved: 'Incident Resolved', ticket_created: 'Ticket Created',
  ticket_mention: 'Ticket Mention', ticket_sla_risk: 'SLA Risk',
  ticket_sla_breach: 'SLA Breach', ticket_unassigned: 'Ticket Unassigned',
};

export default function Notifications() {
  const [tab, setTab]       = useState('rules');
  const [rules, setRules]   = useState([]);
  const [logs, setLogs]     = useState([]);
  const [showModal, setShowModal] = useState(false);

  const loadRules = useCallback(() => notificationService.getRules().then(r => setRules(r.data.rules)), []);
  const loadLogs  = useCallback(() => notificationService.getLogs().then(r => setLogs(r.data.logs)), []);

  useEffect(() => { loadRules(); loadLogs(); }, [loadRules, loadLogs]);

  const handleToggle = async (rule) => {
    await notificationService.updateRule(rule._id, { enabled: !rule.enabled });
    loadRules();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await notificationService.deleteRule(id);
    loadRules();
  };

  return (
    <div className="notif-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-subtitle">Configure alert rules and view delivery logs</p>
        </div>
        {tab === 'rules' && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Add Rule
          </button>
        )}
      </div>

      <div className="notif-tabs">
        <button className={`notif-tab ${tab==='rules' ? 'active' : ''}`} onClick={() => setTab('rules')}>
          <Bell size={14} /> Rules ({rules.length})
        </button>
        <button className={`notif-tab ${tab==='logs' ? 'active' : ''}`} onClick={() => setTab('logs')}>
          Delivery Log ({logs.length})
        </button>
      </div>

      {tab === 'rules' && (
        <div className="rules-list">
          {rules.length === 0 && <div className="card empty-state">No notification rules yet. Add one to start receiving alerts.</div>}
          {rules.map(rule => (
            <div key={rule._id} className={`rule-card card ${!rule.enabled ? 'rule-card--disabled' : ''}`}>
              <div className="rule-card__header">
                <div className="rule-card__title">{rule.name}</div>
                <div className="rule-card__actions">
                  <button className={`toggle-btn ${rule.enabled ? 'on' : 'off'}`} onClick={() => handleToggle(rule)}>
                    {rule.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                  <button className="icon-btn" onClick={() => handleDelete(rule._id)}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="rule-trigger">
                <span className="trigger-badge">{TRIGGER_LABELS[rule.trigger] || rule.trigger}</span>
                {rule.deployment && <span className="scope-tag">Deployment: {rule.deployment.name}</span>}
                {rule.client     && <span className="scope-tag">Client: {rule.client.name}</span>}
                {!rule.deployment && !rule.client && <span className="scope-tag global">Global</span>}
              </div>
              <div className="rule-channels">
                {rule.channels.map((ch, i) => (
                  <div key={i} className="channel-chip">
                    <span className={`ch-type ch-type--${ch.type}`}>{ch.type}</span>
                    <span className="ch-target">{ch.target}</span>
                  </div>
                ))}
              </div>
              {rule.cooldownMinutes && <div className="rule-cooldown">Cooldown: {rule.cooldownMinutes}min</div>}
            </div>
          ))}
        </div>
      )}

      {tab === 'logs' && (
        <div className="card logs-table">
          <table>
            <thead>
              <tr>
                <th>Trigger</th>
                <th>Channel</th>
                <th>Target</th>
                <th>Deployment / Client</th>
                <th>Status</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log._id}>
                  <td><span className="trigger-badge sm">{TRIGGER_LABELS[log.trigger] || log.trigger}</span></td>
                  <td><span className={`ch-type ch-type--${log.channel}`}>{log.channel}</span></td>
                  <td className="log-target">{log.target}</td>
                  <td className="log-client">{log.deployment?.name || log.client?.name || '—'}</td>
                  <td>
                    {log.status === 'sent'
                      ? <span className="log-ok"><CheckCircle size={12} /> Sent</span>
                      : <span className="log-fail"><XCircle size={12} /> Failed</span>
                    }
                  </td>
                  <td className="log-time">{formatDistanceToNow(new Date(log.sentAt), { addSuffix: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && <div className="empty-state">No notification logs yet</div>}
        </div>
      )}

      {showModal && (
        <NotificationRuleModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadRules(); }}
        />
      )}
    </div>
  );
}
